/* ==========================================================================
   TundraLegs™ landing page interactions — vanilla JS, no dependencies.

   TABLE OF CONTENTS
     1. Product gallery (peeking carousel + synced thumbnails)
     2. Size picker + buy buttons
     3. Size-chart cm/in toggle
     4. FAQ accordion
     5. Scroll reveal (progressive enhancement)
     6. Size-finder modal (breed search / paw measurements)
     7. Image lightbox
     8. Sticky mobile buy bar

   SHOPIFY HOOKS — every cart action funnels through addSetsToCart() in
   section 2, which hands the lines to window.tundraAddToCart
   (assets/tundra-cart.js).
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {

  /* ------------------------------------------------------------------------
     0. IN-PAGE ANCHOR LINKS (header nav, rating pill → #reviews etc.)
     Horizon makes .page-wrapper the scroll container on desktop (html/body
     are overflow:hidden ≥990px), and a plain #hash link is also a no-op when
     the hash is already set. Scroll the target ourselves instead.
     ------------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('.tundra-legs a[href^="#"]');
    if (!link) return;
    // A finder trigger (<a href="#size-guide" data-open-finder>) opens the
    // modal and must NOT also scroll the page to #size-guide; the same goes for
    // any link whose own handler already took the click.
    if (e.defaultPrevented || link.hasAttribute('data-open-finder')) return;
    // Finder triggers are <a href="#size-guide"> only as a no-JS fallback; with
    // JS they open the size modal (handled separately) and must not scroll here.
    if (link.hasAttribute('data-open-finder')) return;
    const hash = link.getAttribute('href');
    if (!hash || hash === '#') return;
    let target = null;
    try { target = document.querySelector(hash); } catch (x) { return; }
    // "#reviews" means the Pupster Reviews app block, which lives in its own
    // template section (not inside this one), so it has no id we control.
    if (!target && hash === '#reviews') {
      target = document.querySelector('.shopify-app-block[id*="pu_reviews"]');
    }
    if (!target) return;
    e.preventDefault();
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // A link straight to an FAQ answer opens it before scrolling.
    const details = target.closest && target.closest('details');
    if (details) details.open = true;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    if (history.replaceState) history.replaceState(null, '', hash);
  });

  /* ------------------------------------------------------------------------
     1. PRODUCT GALLERY — peeking swipe carousel with synced thumbnail strip
     ------------------------------------------------------------------------ */
  const galleryTrack = document.getElementById('galleryTrack');
  const slides       = galleryTrack ? galleryTrack.querySelectorAll('.gallery__slide') : [];
  const thumbs       = document.querySelectorAll('.gallery__thumb');
  const galleryPrev  = document.getElementById('galleryPrev');
  const galleryNext  = document.getElementById('galleryNext');
  const thumbPrev    = document.getElementById('thumbPrev');
  const thumbNext    = document.getElementById('thumbNext');
  const thumbsStrip  = document.getElementById('thumbs');

  const slideCount   = slides.length;
  let   currentSlide = 0;

  /** Mark slide i active: highlight its thumb, keep it scrolled into view,
      and disable the stage + thumb arrows at the ends. */
  function setActiveSlide(i) {
    currentSlide = Math.max(0, Math.min(slideCount - 1, i));
    thumbs.forEach((thumb, idx) => {
      thumb.classList.toggle('is-active', idx === currentSlide);
    });
    const activeThumb = thumbs[currentSlide];
    // Center the active thumb inside its OWN horizontal strip only. Using
    // scrollIntoView({block:'nearest'}) could scroll the whole PAGE vertically
    // to pull the gallery back into view — a self-scroll jump on mobile.
    const strip = activeThumb && activeThumb.parentElement;
    if (activeThumb && strip) {
      const thumbRect = activeThumb.getBoundingClientRect();
      const stripRect = strip.getBoundingClientRect();
      const delta = (thumbRect.left - stripRect.left) - (stripRect.width - thumbRect.width) / 2;
      strip.scrollBy({ left: delta, behavior: 'smooth' });
    }
    if (galleryPrev) galleryPrev.disabled = currentSlide === 0;
    if (galleryNext) galleryNext.disabled = currentSlide === slideCount - 1;
    if (thumbPrev)   thumbPrev.disabled   = currentSlide === 0;
    if (thumbNext)   thumbNext.disabled   = currentSlide === slideCount - 1;
  }

  /** Scroll the main carousel to slide i. */
  function goToSlide(i, smooth) {
    if (!galleryTrack || !slides[i]) return;
    galleryTrack.scrollTo({ left: slides[i].offsetLeft, behavior: smooth === false ? 'auto' : 'smooth' });
    setActiveSlide(i);
  }

  thumbs.forEach((thumb, idx) => {
    thumb.addEventListener('click', () => goToSlide(idx));
  });
  if (galleryPrev) galleryPrev.addEventListener('click', () => goToSlide(currentSlide - 1));
  if (galleryNext) galleryNext.addEventListener('click', () => goToSlide(currentSlide + 1));
  if (thumbPrev)   thumbPrev.addEventListener('click',   () => goToSlide(currentSlide - 1));
  if (thumbNext)   thumbNext.addEventListener('click',   () => goToSlide(currentSlide + 1));

  // Keep thumbnails in sync when the user swipes the main carousel.
  if (galleryTrack) {
    let scrollDebounce;
    galleryTrack.addEventListener('scroll', () => {
      clearTimeout(scrollDebounce);
      scrollDebounce = setTimeout(() => {
        // Find whichever slide is closest to the left edge.
        let nearest = 0;
        let best = Infinity;
        slides.forEach((slide, idx) => {
          const d = Math.abs(slide.offsetLeft - galleryTrack.scrollLeft);
          if (d < best) { best = d; nearest = idx; }
        });
        if (nearest !== currentSlide) setActiveSlide(nearest);
      }, 90);
    }, { passive: true });
  }

  // Drop the thumbnail strip's edge fade on whichever side is fully scrolled
  // (nothing more to reveal that way).
  function updateThumbFades() {
    if (!thumbsStrip) return;
    const max = thumbsStrip.scrollWidth - thumbsStrip.clientWidth;
    thumbsStrip.classList.toggle('is-start', thumbsStrip.scrollLeft <= 1);
    thumbsStrip.classList.toggle('is-end',   thumbsStrip.scrollLeft >= max - 1);
  }
  if (thumbsStrip) {
    thumbsStrip.addEventListener('scroll', updateThumbFades, { passive: true });
    window.addEventListener('resize', updateThumbFades);
    updateThumbFades();
  }

  if (slideCount) setActiveSlide(0);

  /* ------------------------------------------------------------------------
     2. SIZE PICKER + BUY BUTTONS — pre-winter BOGO (buy 1 set, get 1 free)
     One shared state drives every CTA on the page:
       firstSize — size of the paid set (required)
       freeMode  — 'same' (free set = first set's size) | 'different'
       freeSize  — size of the free set, only when freeMode is 'different'
       - no first size                → CTAs open the size finder (first set)
       - 'different' but no free size → CTAs open the size finder (free set)
       - complete                     → CTAs add BOTH sets to the cart
     The second set is made free by the store's automatic discount; the page
     never prices it. When sizes differ the customer pays the dearer tier
     (Shopify makes the cheaper item free), so that is the price shown.
     ------------------------------------------------------------------------ */
  const firstPills = document.querySelectorAll('.size-pill[data-set="first"]');
  const freePills  = document.querySelectorAll('.size-pill[data-set="free"]');
  const sizeHelper = document.getElementById('sizeHelper');
  const freeHelper = document.getElementById('freeHelper');
  const freePicker = document.getElementById('freePicker');
  const priceValue = document.getElementById('buyPrice');       // hidden until a size exists
  const buyButton  = document.getElementById('addToCart');      // main buy-box CTA
  const freeRadios = document.querySelectorAll('input[name="free-set"], input[name="free-set-modal"]');
  let   firstSize = null;
  let   freeSize  = null;
  let   freeMode  = 'same';
  let   finderApi = null;   // { open(set), sync() } — set by initSizeFinder (section 6)
  let   addInFlight = false;

  const reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;

  /** Put a buy CTA into / out of a loading state (spinner + "Adding…"). Stashes
      the label so any CTA — main, secondary, or the finder — restores cleanly. */
  function setButtonLoading(btn, on) {
    if (!btn) return;
    if (on) {
      if (btn.dataset.loading) return;
      btn.dataset.loading = '1';
      btn.dataset.label = btn.textContent;
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.innerHTML = '<span class="btn__spin" aria-hidden="true"></span>Adding…';
    } else {
      if (!btn.dataset.loading) return;
      delete btn.dataset.loading;
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
      btn.classList.remove('is-loading');
      if (btn.dataset.label != null) { btn.textContent = btn.dataset.label; delete btn.dataset.label; }
    }
  }

  /** Reveal or collapse a section with a height + fade (no layout snap). Keeps
      the `hidden` attribute authoritative, so a collapsed section is never
      focusable. Reduced motion (or no WAAPI) → instant. */
  function revealSection(el, open) {
    if (!el) return;
    if (reduceMotion || !el.animate) { el.hidden = !open; return; }
    if (open === !el.hidden) return;                 // already in that state
    if (el._revealAnim) el._revealAnim.cancel();
    if (open) el.hidden = false;
    const full = el.scrollHeight;
    el.style.overflow = 'hidden';
    const frames = open
      ? [{ height: '0px', opacity: 0 }, { height: full + 'px', opacity: 1 }]
      : [{ height: full + 'px', opacity: 1 }, { height: '0px', opacity: 0 }];
    el._revealAnim = el.animate(frames, {
      duration: open ? 300 : 230,
      easing: open ? 'cubic-bezier(.22,.61,.36,1)' : 'ease',
    });
    el._revealAnim.onfinish = () => {
      el.style.overflow = '';
      el.style.height = '';
      if (!open) el.hidden = true;
      el._revealAnim = null;
    };
  }

  /** Mirror the chosen first-set size into the free-set recap chip. */
  function updateFreeRecap() {
    const chip = document.getElementById('firstSizeChip');
    const recap = document.getElementById('freeRecap');
    if (chip) chip.textContent = firstSize || '—';
    if (recap) recap.hidden = !firstSize;
  }

  function pillFor(set, size) {
    return document.querySelector('.size-pill[data-set="' + set + '"][data-size="' + size + '"]');
  }

  /** Select a size pill for one set ('first' | 'free') and repaint. */
  function selectPill(set, size) {
    const pills = set === 'first' ? firstPills : freePills;
    let chosen = null;
    pills.forEach((p) => {
      const on = p.getAttribute('data-size') === size;
      p.classList.toggle('is-selected', on);
      p.setAttribute('aria-checked', on ? 'true' : 'false');
      if (on) chosen = p;
    });
    if (set === 'first') firstSize = size; else freeSize = size;
    const helper = set === 'first' ? sizeHelper : freeHelper;
    if (helper && chosen) {
      helper.textContent = chosen.getAttribute('data-helper');
      helper.classList.remove('size-picker__error');
    }
    refreshBuyState();
  }

  /** 'same' | 'different' — keeps page + modal radios and the free picker in step. */
  function setFreeMode(mode, { quiet = false } = {}) {
    freeMode = mode;
    freeRadios.forEach((r) => { r.checked = r.value === mode; });
    if (freePicker) {
      const open = mode === 'different';
      if (open) updateFreeRecap();
      revealSection(freePicker, open);
      if (open && !quiet) {
        setTimeout(() => freePicker.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' }), reduceMotion ? 0 : 140);
        if (!reduceMotion) {
          freePicker.classList.add('just-revealed');
          setTimeout(() => freePicker.classList.remove('just-revealed'), 1200);
        }
      }
    }
    refreshBuyState();
  }

  /** [paid size, free size] once the selection is complete, else null. */
  function cartSizes() {
    if (!firstSize) return null;
    if (freeMode === 'same') return [firstSize, firstSize];
    return freeSize ? [firstSize, freeSize] : null;
  }

  /** Pill whose price the customer pays: the dearer of the chosen sizes. */
  function pricePill() {
    const sizes = cartSizes() || (firstSize ? [firstSize] : []);
    let best = null;
    sizes.forEach((s) => {
      const p = pillFor('first', s);
      if (p && (!best || Number(p.dataset.cents) > Number(best.dataset.cents))) best = p;
    });
    return best;
  }

  /** Repaint price slots and the buy-box CTA from the current state. */
  function refreshBuyState() {
    const pill = pricePill();
    if (priceValue) priceValue.hidden = !pill;
    if (pill) {
      document.querySelectorAll('.tundra-legs [data-tl="now"]').forEach((el) => { el.textContent = pill.dataset.now; });
    }
    if (buyButton && !buyButton.dataset.loading) {
      if (!firstSize)         buyButton.textContent = 'Help me Choose size';
      else if (!cartSizes())  buyButton.textContent = 'Pick the free set’s size';
      else                    buyButton.textContent = 'Add 2 Sets to Cart — ' + pill.dataset.now;
    }
    updateFreeRecap();
    if (finderApi) finderApi.sync();
  }

  [['first', firstPills], ['free', freePills]].forEach(([set, pills]) => {
    pills.forEach((pill) => {
      pill.setAttribute('role', 'radio');
      pill.setAttribute('aria-checked', 'false');
      pill.addEventListener('click', () => selectPill(set, pill.getAttribute('data-size')));
    });
  });
  freeRadios.forEach((r) => r.addEventListener('change', () => { if (r.checked) setFreeMode(r.value); }));

  /* Browsers restore form controls on reload and on back/forward (e.g. back
     from checkout), so the "Different size" radio can come back checked while
     this script started in 'same' mode — the free-set picker then stayed
     hidden until the radio was toggled (Yerdana, 2026-10-30). Read the
     restored state and open the picker quietly: no scroll, no flash. */
  function syncFreeModeFromRadios() {
    const checked = document.querySelector('input[name="free-set"]:checked');
    if (checked && checked.value !== freeMode) setFreeMode(checked.value, { quiet: true });
  }
  syncFreeModeFromRadios();
  window.addEventListener('pageshow', syncFreeModeFromRadios);

  /* Shopify cart integration: window.tundraAddToCart (assets/tundra-cart.js,
     a module) adds the lines and opens Horizon's cart drawer already
     populated — no empty-then-item flash. Same size twice = one line, qty 2. */
  function addSetsToCart() {
    const sizes = cartSizes();
    if (!sizes) return Promise.resolve();
    const items = [];
    sizes.forEach((s) => {
      const pill = pillFor('first', s);
      const id = pill && pill.getAttribute('data-variant-id');
      if (!id) return;
      const row = items.find((i) => i.id === id);
      if (row) row.quantity += 1; else items.push({ id: id, quantity: 1 });
    });
    if (items.length && window.tundraAddToCart) return window.tundraAddToCart(items);
    return Promise.resolve();
  }

  /** Add both sets with a loading state on the triggering + main CTA; a single
      in-flight guard blocks any double-add across every buy CTA on the page. */
  async function doAddToCart(triggerBtn) {
    if (addInFlight) return;
    addInFlight = true;
    const btns = [buyButton, triggerBtn].filter((b, i, a) => b && a.indexOf(b) === i);
    btns.forEach((b) => setButtonLoading(b, true));
    try { await addSetsToCart(); }
    finally {
      btns.forEach((b) => setButtonLoading(b, false));
      addInFlight = false;
    }
  }

  /** Main buy-box CTA: the inline add path. We honor the on-page size pick — no
      size yet → open the finder to help choose; sizes chosen → add straight to
      cart (a quick single-size pick is still a valid buy). */
  function handleMainBuy() {
    if (!firstSize)   { if (finderApi) finderApi.open('first'); return; }
    if (!cartSizes()) { if (finderApi) finderApi.open('free');  return; }
    doAddToCart(buyButton);
  }

  /** Scattered CTAs (offer section, final band, sticky bar) always funnel into
      the guided size modal — cold traffic shouldn't buy from down the page
      without going through sizing. The top coupon bar just scrolls (it's an
      <a href="#buy">, not wired here). */
  function handleSecondaryBuy() {
    if (finderApi) finderApi.open('first');
  }

  if (buyButton) buyButton.addEventListener('click', handleMainBuy);
  document.querySelectorAll('.js-buy').forEach((btn) => {
    btn.addEventListener('click', handleSecondaryBuy);
  });
  refreshBuyState();

  /* ------------------------------------------------------------------------
     3. SIZE-CHART UNIT TOGGLE (cm / inches tables in the size guide)
     ------------------------------------------------------------------------ */
  const chartToggle = document.getElementById('unitToggle');
  if (chartToggle) {
    const chartCm = document.getElementById('chart-cm');
    const chartIn = document.getElementById('chart-in');
    chartToggle.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', () => {
        chartToggle.querySelectorAll('button').forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        const showCm = btn.getAttribute('data-unit') === 'cm';
        chartCm.hidden = !showCm;
        chartIn.hidden = showCm;
      });
    });
  }

  /* ------------------------------------------------------------------------
     4. FAQ ACCORDION — opening one item closes the others
     ------------------------------------------------------------------------ */
  const faqItems = document.querySelectorAll('.faq details');
  faqItems.forEach((item) => {
    item.addEventListener('toggle', () => {
      if (item.open) {
        faqItems.forEach((other) => {
          if (other !== item) other.open = false;
        });
      }
    });
  });

  /* ------------------------------------------------------------------------
     5. SCROLL REVEAL (progressive enhancement)
     The .reveal class is added HERE in JS, so users without JS — or without
     IntersectionObserver — always see content at full opacity.
     ------------------------------------------------------------------------ */
  const prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!prefersReducedMotion && 'IntersectionObserver' in window) {
    const revealSelectors = [
      '.proof-strip__inner', '.section-head', '.problem-card', '.feature',
      '.split__card', '.media-grid', '.table-scroll',
      '.size-rule', '.unit-toggle', '.value-stack', '.review-card',
      '.faq details', '.final-cta__copy'
    ];
    const revealEls = document.querySelectorAll(revealSelectors.join(','));

    const revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    revealEls.forEach((el, i) => {
      el.classList.add('reveal');
      el.style.transitionDelay = ((i % 4) * 70) + 'ms';   // gentle stagger
      revealObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     6. SIZE-FINDER MODAL
     Two methods: breed lookup (quick start) or paw measurements (accurate).
     Sizing runs on the two paw measurements — the boot sole is the fixed
     dimension; the TundraFit harness adjusts to the body.
     ------------------------------------------------------------------------ */
  (function initSizeFinder() {
    const overlay = document.getElementById('finderModal');
    if (!overlay) return;

    const modal       = overlay.querySelector('.finder-modal');
    const closeBtn    = overlay.querySelector('[data-close-finder]');
    const lenInput    = document.getElementById('inLen');
    const widInput    = document.getElementById('inWid');
    const unitToggle  = document.getElementById('finderUnit');
    const unitLabels  = overlay.querySelectorAll('[data-unit-label]');
    const resultCard  = document.getElementById('finderResult');
    const resultSize  = document.getElementById('finderSize');
    const resultWhy   = document.getElementById('finderWhy');
    const resultBreeds = document.getElementById('finderBreeds');
    const useBtn      = document.getElementById('finderUse');
    const tabs        = overlay.querySelectorAll('.finder-tab');
    const panels      = overlay.querySelectorAll('.finder-panel');
    const breedSearch = document.getElementById('breedSearch');
    const breedList   = document.getElementById('breedList');
    const finderTitle = document.getElementById('finderTitle');
    const finderSub   = document.getElementById('finderSub');
    const finderStep  = document.getElementById('finderStep');
    const finderFree  = document.getElementById('finderFreeSet');   // same/different radio in the result card

    /* Official paw-size data (cm). Metric is the source of truth.
       SHOPIFY HOOK: mirror any size-chart changes here AND in the tables
       in index.html — this array drives the recommendation. */
    const SIZES = [
      { size: 'XS',  len: [3.5, 4.6],  wid: [2.5, 3.8], breeds: 'Small Teddy, Small Japanese Spitz' },
      { size: 'S',   len: [4.5, 6],    wid: [3.5, 4.6], breeds: 'Mini Schnauzer, Bedlington Terrier, Bichon Frise' },
      { size: 'M',   len: [6, 7],      wid: [5.1, 6.3], breeds: 'Mini Bulldog, Whippet' },
      { size: 'L',   len: [7, 8.5],    wid: [6.3, 7.2], breeds: 'Bulldog, Pit Bull, Portuguese Water Dog' },
      { size: 'XL',  len: [8.6, 9.4],  wid: [7.2, 8.2], breeds: 'German Shepherd, Labrador, Husky' },
      { size: '2XL', len: [9.5, 10.5], wid: [8.2, 9.5], breeds: 'Doberman, Golden Retriever, Akita' }
    ];
    const DIM_LABEL = { len: 'foot length', wid: 'foot width' };
    const PLACEHOLDER = {
      cm: { len: 'e.g. 7',   wid: 'e.g. 6.5' },
      in: { len: 'e.g. 2.8', wid: 'e.g. 2.6' }
    };

    /* Breed list + matching now live in assets/tundra-breeds.js
       (window.TundraBreeds) — the single source for breeds and search. */

    let unit = 'in';                 // inches is the default
    let recommendedSize = null;
    let lastFocused = null;
    let finderSet = 'first';         // which set is being sized: 'first' | 'free'
    const reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---- Sizing math ---------------------------------------------------- */

    /** For one measurement (cm): the size band it belongs in. Overlapping
        bands resolve UP (the "between sizes, size up" rule); values falling
        in a gap between bands round up to the next size. */
    function sizeIndexFor(valueCm, dim) {
      const last = SIZES.length - 1;
      const contained = [];
      for (let i = 0; i < SIZES.length; i++) {
        if (valueCm >= SIZES[i][dim][0] && valueCm <= SIZES[i][dim][1]) contained.push(i);
      }
      if (contained.length) return { idx: contained[contained.length - 1], status: 'ok' };
      if (valueCm < SIZES[0][dim][0])    return { idx: 0,    status: 'under' };
      if (valueCm > SIZES[last][dim][1]) return { idx: last, status: 'over' };
      for (let j = 0; j < SIZES.length; j++) {
        if (SIZES[j][dim][0] > valueCm) return { idx: j, status: 'ok' };
      }
      return { idx: last, status: 'ok' };
    }

    function toCm(v) { return unit === 'in' ? v * 2.54 : v; }

    /** Recompute the recommendation from the two paw inputs. Both are
        required; the larger of the two bands wins so nothing pinches. */
    function computeFromMeasurements() {
      const len = parseFloat(lenInput.value);
      const wid = parseFloat(widInput.value);
      if (!(len > 0) || !(wid > 0)) { clearResult(); return; }

      const entries = [
        { dim: 'len', res: sizeIndexFor(toCm(len), 'len') },
        { dim: 'wid', res: sizeIndexFor(toCm(wid), 'wid') }
      ];

      let maxIdx = 0;
      let oversize = false;
      entries.forEach((e) => {
        if (e.res.idx > maxIdx) maxIdx = e.res.idx;
        if (e.res.status === 'over') oversize = true;
      });

      const drivers = entries.filter((e) => e.res.idx === maxIdx)
                             .map((e) => DIM_LABEL[e.dim]);
      let why;
      if (drivers.length === entries.length) {
        why = 'Both measurements land in size <strong>' + SIZES[maxIdx].size + '</strong> — a confident fit.';
      } else {
        why = 'Your ' + drivers[0] + ' is the deciding measurement, so we size to <strong>' +
              SIZES[maxIdx].size + '</strong> so nothing pinches.';
      }
      renderResult(maxIdx, why, oversize);
    }

    function renderResult(idx, whyHtml, oversize) {
      recommendedSize = SIZES[idx].size;
      resultCard.classList.remove('is-stale');   // a fresh result is never stale
      if (useBtn) useBtn.disabled = false;
      resultSize.textContent = recommendedSize;
      resultWhy.innerHTML = whyHtml + (oversize
        ? ' <br>Your dog is at the very top of our range — if you’re unsure, reach out before ordering.'
        : '');
      resultBreeds.innerHTML = 'Similar dogs at this size: ' + SIZES[idx].breeds + '.';
      resultCard.classList.toggle('is-oversize', !!oversize);
      resultCard.hidden = false;
      if (!reduceMotion) {
        resultCard.setAttribute('data-animate', 'in');
        setTimeout(() => resultCard.removeAttribute('data-animate'), 400);
      }
    }

    function clearResult() {
      recommendedSize = null;
      delete resultCard.dataset.breed;
      resultCard.hidden = true;
    }

    /** After a breed pick, typing in the search box must NOT hide the result
        card — that made the whole modal jump on every keystroke. Instead the
        card stays put but dims, and its CTA disarms, until the text matches
        the picked breed again (or a new breed is picked). */
    function syncResultStale() {
      if (resultCard.hidden || !resultCard.dataset.breed) return;
      const stale = breedSearch.value.trim().toLowerCase() !== resultCard.dataset.breed.toLowerCase();
      resultCard.classList.toggle('is-stale', stale);
      if (useBtn) useBtn.disabled = stale;
    }

    /* ---- Measurement inputs --------------------------------------------- */
    [lenInput, widInput].forEach((input) => {
      if (!input) return;
      input.addEventListener('input', computeFromMeasurements);
      input.addEventListener('focus', () => {
        overlay.querySelectorAll('.finder-field').forEach((f) => f.classList.remove('is-focus'));
        const field = input.closest('.finder-field');
        if (field) field.classList.add('is-focus');
      });
    });

    /* ---- Breed search ---------------------------------------------------- */
    function renderBreedList(query) {
      if (!breedList) return;
      const q = (query || '').trim();
      const matches = (window.TundraBreeds && window.TundraBreeds.search)
        ? window.TundraBreeds.search(q)
        : [];
      breedList.innerHTML = '';
      if (!matches.length) {
        const empty = document.createElement('li');
        empty.className = 'breed-list__empty';
        empty.innerHTML = 'No match — check the spelling, or <button class="finder-linkbtn" data-switch="measure" type="button">measure instead →</button>';
        breedList.appendChild(empty);
        wireMethodSwitchLinks();
        return;
      }
      matches.forEach((breed) => {
        const li  = document.createElement('li');
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'breed-list__row';
        row.setAttribute('role', 'option');
        row.innerHTML = '<span class="breed-list__name">' + breed.name + '</span>' +
                        '<span class="breed-list__size">' + breed.size + '</span>';
        row.addEventListener('click', () => selectBreed(breed));
        li.appendChild(row);
        breedList.appendChild(li);
      });
    }

    function selectBreed(breed) {
      if (breedSearch) { breedSearch.value = breed.name; breedSearch.blur(); } // dismiss the keyboard
      if (breedList) breedList.innerHTML = '';
      const idx = SIZES.findIndex((s) => s.size === breed.size);
      if (idx < 0) { clearResult(); return; }
      renderResult(idx,
        'At their adult size, <strong>' + breed.name + '</strong> typically wears size <strong>' + breed.size +
        '</strong>. For a mixed or in-between build, measuring is the surest way.', false);
      resultCard.dataset.breed = breed.name;   // staleness reference for syncResultStale
      // Guide the eye to the choices + CTA at the bottom of the modal: scroll
      // the modal fully down so the free-set options and the Add-to-Cart button
      // are as visible as possible (works for both the first and free-set
      // steps). Only on breed pick, not on measurement typing.
      const scroller = overlay.querySelector('.finder-modal__scroll');
      if (scroller) {
        const toBottom = () => scroller.scrollTo({ top: scroller.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' });
        toBottom();
        // Dismissing the mobile keyboard (blur above) grows the modal back a
        // moment later — re-pin to the bottom so the CTA stays in view.
        if (!reduceMotion) setTimeout(toBottom, 260);
      }
    }

    if (breedSearch) {
      // Re-filter the list on keystrokes, but keep the result card on screen
      // (hiding it made the modal jump); it just dims via syncResultStale.
      breedSearch.addEventListener('input', () => { renderBreedList(breedSearch.value); syncResultStale(); });
      breedSearch.addEventListener('focus', () => renderBreedList(breedSearch.value));
    }

    /* ---- Unit toggle (default: inches) ----------------------------------- */
    if (unitToggle) {
      unitToggle.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
          const newUnit = btn.getAttribute('data-unit');
          if (newUnit === unit) return;
          [lenInput, widInput].forEach((input) => {
            const v = parseFloat(input.value);
            if (!isNaN(v)) input.value = (newUnit === 'in' ? v / 2.54 : v * 2.54).toFixed(1);
          });
          unit = newUnit;
          unitToggle.querySelectorAll('button').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          unitLabels.forEach((el) => { el.textContent = unit; });
          lenInput.placeholder = PLACEHOLDER[unit].len;
          widInput.placeholder = PLACEHOLDER[unit].wid;
          computeFromMeasurements();
        });
      });
    }

    /* ---- Method tabs (breed / measure) ------------------------------------ */
    function setFinderMethod(method) {
      tabs.forEach((tab) => {
        const active = tab.getAttribute('data-method') === method;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panels.forEach((panel) => { panel.hidden = panel.getAttribute('data-panel') !== method; });
      if (method === 'measure') {
        computeFromMeasurements();
      } else {
        renderBreedList(breedSearch ? breedSearch.value : '');
        clearResult();
      }
    }
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => setFinderMethod(tab.getAttribute('data-method')));
    });
    /** "Measure instead →" links (incl. ones created inside the breed list). */
    function wireMethodSwitchLinks() {
      overlay.querySelectorAll('[data-switch]').forEach((btn) => {
        if (btn._switchWired) return;
        btn._switchWired = true;
        btn.addEventListener('click', () => setFinderMethod(btn.getAttribute('data-switch')));
      });
    }
    wireMethodSwitchLinks();

    /* ---- Step copy + result CTA label follow the BOGO state ----------------- */
    function syncFinderCopy() {
      const free = finderSet === 'free';
      if (finderTitle) finderTitle.textContent = free ? 'Pick size of the Free set' : 'Find my size';
      if (finderSub) {
        finderSub.hidden = !(free && firstSize);
        finderSub.textContent = free && firstSize ? 'Your first set is size ' + firstSize + '. Now size the free one.' : '';
      }
      if (finderStep) {
        const twoStep = freeMode === 'different';
        finderStep.hidden = !twoStep;
        finderStep.textContent = twoStep ? (free ? 'Step 2 of 2' : 'Step 1 of 2') : '';
      }
      if (finderFree) finderFree.hidden = free;
      if (useBtn) {
        useBtn.textContent = (!free && freeMode === 'different')
          ? 'Next: free-set size →'
          : 'Add to Cart';
      }
    }

    /* ---- Result CTA: select the pill on the page, then add / go to step 2 --- */
    if (useBtn) useBtn.addEventListener('click', () => {
      if (!recommendedSize || useBtn.dataset.loading) return;
      const chosen = recommendedSize;
      if (finderSet === 'free') {
        selectPill('free', chosen);
        closeFinder();
        doAddToCart(useBtn);
        return;
      }
      selectPill('first', chosen);
      if (freeMode === 'different') {
        // Two-step flow: same finder, re-staged for the free set.
        stageFinder('free');
        return;
      }
      closeFinder();
      /* SHOPIFY HOOK: funnels through doAddToCart() — see section 2. */
      doAddToCart(useBtn);
    });

    /* ---- Open / close with animation + focus management ------------------- */
    overlay.removeAttribute('hidden');   // 'hidden' is only the no-JS fallback

    function onKeydown(e) {
      if (e.key === 'Escape') { closeFinder(); return; }
      if (e.key !== 'Tab') return;
      // Simple focus trap inside the dialog.
      const focusables = Array.prototype.filter.call(
        modal.querySelectorAll('button, input, select, a[href], [tabindex]:not([tabindex="-1"])'),
        (el) => el.offsetParent !== null && !el.disabled
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last  = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first)      { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /** Reset the finder for one set: fresh search, breed tab, scrolled to top. */
    function stageFinder(set) {
      finderSet = set;
      overlay.querySelectorAll('.finder-field').forEach((f) => f.classList.remove('is-focus'));
      const scroller = overlay.querySelector('.finder-modal__scroll');
      if (scroller) scroller.scrollTop = 0;
      if (breedSearch) breedSearch.value = '';   // start fresh, not on the last search
      [lenInput, widInput].forEach((i) => { if (i) i.value = ''; });
      setFinderMethod('breed');                  // always open on the "By breed" tab
      syncFinderCopy();
      if (modal && !reduceMotion) {
        modal.setAttribute('data-step', set);
        setTimeout(() => modal.removeAttribute('data-step'), 400);
      }
    }

    let finderScrollY = 0;
    function openFinder(set) {
      lastFocused = document.activeElement;
      finderScrollY = window.scrollY;
      stageFinder(set || 'first');
      document.body.classList.add('finder-open');
      overlay.classList.add('is-open');
      document.addEventListener('keydown', onKeydown);
      // Focus the dialog itself (not an input) so the header stays in view and
      // the mobile keyboard doesn't pop before the user is ready.
      if (modal) modal.setAttribute('tabindex', '-1');
      setTimeout(() => (modal || closeBtn).focus(), 80);
    }

    function closeFinder() {
      overlay.classList.remove('is-open');
      fkbStop();
      document.removeEventListener('keydown', onKeydown);
      const done = () => {
        document.body.classList.remove('finder-open');
        // body was overflow:hidden the whole time, so a changed scrollY is the
        // OS's doing (iOS keyboard) — restore it, which also re-seats fixed
        // elements (sticky buy bar, cart drawer) that iOS left displaced.
        if (Math.abs(window.scrollY - finderScrollY) > 1) window.scrollTo(0, finderScrollY);
        if (lastFocused && lastFocused.focus) {
          try { lastFocused.focus({ preventScroll: true }); } catch (x) { lastFocused.focus(); }
        }
      };
      if (reduceMotion) { done(); return; }
      // Wait for the fade-out transition, with a timeout fallback.
      let settled = false;
      const onEnd = (e) => {
        if (e.target !== overlay) return;
        settled = true;
        overlay.removeEventListener('transitionend', onEnd);
        done();
      };
      overlay.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (!settled) { overlay.removeEventListener('transitionend', onEnd); done(); }
      }, 380);
    }

    document.querySelectorAll('[data-open-finder]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        openFinder(trigger.getAttribute('data-set') || 'first');
      });
    });
    finderApi = { open: openFinder, sync: syncFinderCopy };
    syncFinderCopy();
    if (closeBtn) closeBtn.addEventListener('click', closeFinder);
    // Close on a backdrop click only when the press STARTED on the backdrop too.
    // Tapping a field can shrink/reflow the modal (the measure tab hides its big
    // diagrams on focus), which moves the content up and out from under the
    // finger — so the follow-up click would land on the newly-exposed backdrop
    // and dismiss the modal the instant the user tapped an input.
    let overlayDownOnSelf = false;
    overlay.addEventListener('pointerdown', (e) => { overlayDownOnSelf = (e.target === overlay); });
    overlay.addEventListener('click', (e) => { if (e.target === overlay && overlayDownOnSelf) closeFinder(); });

    /* ---- keyboard-aware modal (iOS/WebKit) ----
       Shrink the overlay to the space above the on-screen keyboard (VisualViewport
       API) so the centered modal recenters in view and the focused input never
       hides behind the keyboard. No-ops where VisualViewport is unavailable. */
    let fkbTrack = null;
    function fkbApply() {
      const vv = window.visualViewport;
      if (!vv) return;
      const m = overlay.querySelector('.finder-modal');
      // Keep the overlay FULL-SCREEN (inset:0 from CSS) so its scrim always
      // covers the whole page — no peek-through around the modal. Only the MODAL
      // needs to move: when the keyboard opens, iOS and in-app browsers scroll
      // the layout viewport up, so the top-anchored modal would sit above the
      // visible region. Push it back down by padding the overlay's top with the
      // visual-viewport offset, and cap the modal to the visible height.
      overlay.style.paddingTop = (vv.offsetTop + 12) + 'px';
      if (m) m.style.maxHeight = Math.max(200, Math.round(vv.height - 24)) + 'px';
    }
    function fkbStop() {
      const vv = window.visualViewport;
      if (vv && fkbTrack) { vv.removeEventListener('resize', fkbTrack); vv.removeEventListener('scroll', fkbTrack); }
      fkbTrack = null;
      overlay.classList.remove('finder-kb');
      overlay.style.paddingTop = '';
      const m = overlay.querySelector('.finder-modal');
      if (m) m.style.maxHeight = '';
    }
    function fkbStart() {
      fkbApply();
      overlay.classList.add('finder-kb');
      const vv = window.visualViewport;
      if (!vv || fkbTrack) return;
      fkbTrack = fkbApply;
      vv.addEventListener('resize', fkbTrack);
      vv.addEventListener('scroll', fkbTrack);
    }
    overlay.addEventListener('focusin', (e) => {
      if (!e.target.closest || !e.target.closest('input, textarea')) return;
      fkbStart();
      const el = e.target;
      const scroller = overlay.querySelector('.finder-modal__scroll');
      // Pin the focused field to the TOP of the modal so its input + results get
      // the full height above the keyboard. The head is hidden under .finder-kb,
      // and on the free-set step that head is tall (title + subtitle + step), so
      // hiding it shifts the content up and a single smooth scroll gets overridden
      // by the OS's own "scroll the focused input into view" — leaving the search
      // scrolled off the top (the free-set bug). So set scrollTop directly
      // (instant) and re-assert it across the keyboard's open animation, which
      // closes well before a user would start scrolling the list.
      const pin = () => {
        try {
          if (!scroller) return;
          const field = el.closest('.finder-field') || el;
          const delta = field.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
          if (Math.abs(delta - 8) > 2) scroller.scrollTop += (delta - 8);
        } catch (x) {}
      };
      pin();
      [90, 200, 340, 480].forEach((t) => setTimeout(pin, t));
    });
    overlay.addEventListener('focusout', (e) => {
      if (!e.target.closest || !e.target.closest('input, textarea')) return;
      setTimeout(() => {
        const a = document.activeElement;
        if (a && a.closest && a.closest('#finderModal input, #finderModal textarea')) return;
        fkbStop();
      }, 120);
    });
  })();

  /* ------------------------------------------------------------------------
     7. IMAGE LIGHTBOX
     Any element with [data-zoom][data-zoom-img] opens a full-size view of
     that image; [data-zoom-cap] supplies the caption beneath it.
     ------------------------------------------------------------------------ */
  (function initLightbox() {
    const lightbox = document.getElementById('lightbox');
    if (!lightbox) return;
    const lbFrame   = document.getElementById('lightboxImg');
    const lbCaption = document.getElementById('lightboxCap');
    const lbClose   = lightbox.querySelector('[data-close-lightbox]');

    lightbox.removeAttribute('hidden');   // 'hidden' is only the no-JS fallback

    function openLightbox(imgSrc, alt, caption) {
      if (lbFrame)   lbFrame.innerHTML = '<img src="' + imgSrc + '" alt="' + alt + '">';
      if (lbCaption) lbCaption.textContent = caption || '';
      document.body.classList.add('lightbox-open');
      lightbox.classList.add('is-open');
      document.addEventListener('keydown', onKeydown, true);
    }
    function closeLightbox() {
      lightbox.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown, true);
      document.body.classList.remove('lightbox-open');
    }
    function onKeydown(e) {
      if (e.key === 'Escape') { e.stopPropagation(); closeLightbox(); }
    }

    document.querySelectorAll('[data-zoom][data-zoom-img]').forEach((el) => {
      el.addEventListener('click', () => {
        const title = el.getAttribute('data-zoom-title') || 'Diagram';
        openLightbox(el.getAttribute('data-zoom-img'), title + ' — enlarged diagram',
                     el.getAttribute('data-zoom-cap') || '');
      });
    });

    if (lbClose) lbClose.addEventListener('click', closeLightbox);
    lightbox.addEventListener('click', (e) => { if (e.target === lightbox) closeLightbox(); });
  })();

  /* ------------------------------------------------------------------------
     8. STICKY MOBILE BUY BAR — hidden until the user scrolls to the
     [data-sticky-trigger] image, then slides up. Without JS (or without
     IntersectionObserver) the bar simply stays visible.
     ------------------------------------------------------------------------ */
  (function initStickyBuyBar() {
    const bar = document.querySelector('.mobile-buy-bar');
    const trigger = document.querySelector('[data-sticky-trigger]');
    if (!bar) return;
    if (!trigger || !('IntersectionObserver' in window)) return;

    bar.classList.add('is-armed');   // hide it, ready to slide up
    const barObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Show once the trigger is reached — in view, or scrolled past.
        const show = entry.isIntersecting || entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
      });
    }, { threshold: 0, rootMargin: '0px 0px -40% 0px' });
    barObserver.observe(trigger);
  })();

});
